import React from 'react';
import Highcharts from 'highcharts';

import { HER_MODULES } from '../constants/common';

import { getCamelCaseString, isEmptyObject } from './helper';

let modulesMapping = {};
modulesMapping[`${HER_MODULES.HEADER}`] = 'herHeader/HERHeaderContainer';
modulesMapping[`${HER_MODULES.SHC_EFFICIENT}`] = 'EfficientUserContainer';
modulesMapping[`${HER_MODULES.USAGE_SUMMARY}`] = 'SpentOnApplianceContainer';
modulesMapping[`${HER_MODULES.APPLIANCE_SHC_GRAPH_AND_WIDGET}`] = 'SHCContainer';
modulesMapping[`${HER_MODULES.SPONSORED_RECO}`] = 'SponsoredRecoContainer';
modulesMapping[`${HER_MODULES.PERSONALIZED_TIPS}`] = 'PersonalizedTipsContainer';
modulesMapping[`${HER_MODULES.HVAC_INSIGHTS}`] = 'SHCHVACInsightContainer';
modulesMapping[`${HER_MODULES.FAQ}`] = 'FAQContainer';
modulesMapping['PAGE_BREAK'] = 'PageBreak';
modulesMapping[`${HER_MODULES.FOOTER}`] = 'herFooter/HERFooter';
modulesMapping['KEYLINE'] = 'HERFileName';

let translationsData = null;

export const prepareHerChunks = (modulesOrder) => {
  let herModules = [];
  let chunks = [];
  const fileNameModule = 'KEYLINE';
  const pageBreakModule = 'PAGE_BREAK';
  const footerModule = 'FOOTER';
  let displayFooter = modulesOrder.includes(footerModule);

  if (displayFooter) {
    modulesOrder = getUpdatedHERModulesOrder(modulesOrder, footerModule);
  }

  modulesOrder.forEach((order, index) => {
    herModules.push({
      module: React.lazy(() => import (`../components/HER/${modulesMapping[order]}`)),
      name: order
    });

    if (herModules.length === 3 || index === modulesOrder.length - 1) {
      if ((index !== (modulesOrder.length - 1))) {
        // insert file name static module at the end of the first page
        herModules.push({
          module: React.lazy(() => import (`../components/HER/${modulesMapping[fileNameModule]}`)),
          name: fileNameModule
        });
        // insert page break
        herModules.push({
          module: React.lazy(() => import (`../components/HER/${modulesMapping[pageBreakModule]}`)),
          name: pageBreakModule
        });
      } else if(displayFooter) {
        // insert HER footer module at last position
        herModules.push({
          module: React.lazy(() => import (`../components/HER/${modulesMapping[footerModule]}`)),
          name: footerModule
        });
      }
      chunks.push(herModules);

      // reset HER modules as we show only 3 modules per page
      herModules = [];
    }
  });
  return chunks;
};

const getUpdatedHERModulesOrder = (herModules, footerModule)=> {
  const index = herModules.indexOf(footerModule);
  if (index >= -1) {
    herModules.splice(index, 1);
  }
  return herModules;
};

export const getDataForPdfSanity = (herModules, queryParams, translations)  => {
  translationsData = translations;
  let scrappedData = {
    'herMetaData': {
      'measurementType': queryParams.measurementType,
      'seasonType': queryParams.seasonType,
      'locale': queryParams.locale,
      'herModulesOrder': herModules
    }
  };
  for (let i = 0; i < herModules.length; i++) {
    const herModule = herModules[i];
    let elementsToBeScrapped = null;
    const herModuleElement = document.querySelector(`#${herModule}`);
    const elementsMetaConfig = herModuleElement ? herModuleElement.dataset.meta_config : null;

    if (elementsMetaConfig) {
      elementsToBeScrapped = JSON.parse(elementsMetaConfig);
      scrappedData[`${herModule}`] = {};
      elementsToBeScrapped.forEach(moduleConfig => {
        let isString = typeof moduleConfig === 'string';
        let keySelector = getCamelCaseString(isString ? moduleConfig : moduleConfig.selector);
        let data = isString ? getElementTextContentByAttribute(herModule, moduleConfig) : processModule(herModule, moduleConfig);
        if (data) {
          scrappedData[`${herModule}`][`${keySelector}`] = data;
        }
      });
    }
  }
  return scrappedData;
};

const getElementTextContentByAttribute = (herModule, attributeName, elementSelector) => {
  elementSelector = elementSelector || document;
  let element = elementSelector.querySelector(`#${herModule} [data-${attributeName}]`);
  return element ? convertHtmlToText(element.textContent) : null;
}
const getElementByAttribute = (herModule, attributeName, elementSelector) => {
  elementSelector = elementSelector || document;
  return elementSelector.querySelector(`#${herModule} [data-${attributeName}]`) || null;
};

const getAllElementsByAttribute = (herModule, attributeName, elementSelector) => {
  elementSelector = elementSelector || document;
  return elementSelector.querySelectorAll(`#${herModule} [data-${attributeName}]`) || null;
};

const getImageContentByAttribute = (herModule, attributeName, elementSelector) => {
  elementSelector = elementSelector || document;
  return elementSelector.querySelector(`#${herModule} [data-${attributeName}]`) ? elementSelector.querySelector(`#${herModule} [data-${attributeName}]`).attributes.src.value : null;
};

const processModule = (herModule, moduleConfig, elementSelector) => {
  let dataToReturn = {};
  switch (moduleConfig.type) {
    case 'list':
      dataToReturn = [];
      const elements = getAllElementsByAttribute(herModule, moduleConfig.selector);

      elements.forEach(element => {
        let data = {};
        for (let index = 0; index < moduleConfig.elements.length; index++) {
          const dataSelector = moduleConfig.elements[index];
          let isString = typeof dataSelector === 'string';
          let keySelector = getCamelCaseString(isString ? dataSelector : dataSelector.selector);
          let elementContent = isString ? getElementTextContentByAttribute(herModule, dataSelector, element) : processModule(herModule, dataSelector, element);
          if (elementContent) {
            data[`${keySelector}`] = elementContent;
          }
        }
        dataToReturn.push(data);
      });
      break;
    case 'element':
      let element = getElementByAttribute(herModule, moduleConfig.selector);
      for (let index = 0; index < moduleConfig.elements.length; index++) {
        const dataSelector = moduleConfig.elements[index];
        let isString = typeof dataSelector === 'string';
        let keySelector = getCamelCaseString(isString ? dataSelector : dataSelector.selector);
        let elementContent = isString ? getElementTextContentByAttribute(herModule, dataSelector, element) : processModule(herModule, dataSelector, element);
        if (elementContent) {
          dataToReturn[`${keySelector}`] = elementContent;
        }
      }
      break;
    case 'image':
      dataToReturn = getImageContentByAttribute(herModule, moduleConfig.selector, elementSelector);
      break;
    case 'highchart':
      let attributeName = moduleConfig.selector;
      const chartContainer = getElementByAttribute(herModule,attributeName);
      const chartElement = chartContainer ? chartContainer.firstElementChild : null;
      if (chartElement) {
        const chartIndex = chartElement.getAttribute('data-highcharts-chart');
        dataToReturn = processChartOptions(Highcharts.charts[chartIndex], attributeName);
      }
      break;
    default:
      break;
  }
  return isEmptyObject(dataToReturn) ? null : dataToReturn;
};

const processChartOptions = (chartObj, chartModule) => {
  if (!chartObj) {
    return;
  }
  let chartOptions = chartObj.userOptions;
  let chartData = {};
  let legendsData = [];
  if (chartOptions.hasOwnProperty('title')) {
    chartData['title'] = chartOptions.title.text;
  }

  if (chartOptions.hasOwnProperty('subtitle')) {
    chartData['duration'] = chartOptions.subtitle.text;
  }

  if (chartOptions.hasOwnProperty('xAxis') && chartOptions.xAxis.hasOwnProperty('categories')) {
    chartData['xAxisValues'] = chartOptions.xAxis.categories;
  }

  if (chartObj.legend.hasOwnProperty('allItems')) {
    chartObj.legend.allItems.forEach(legendItem => {
      legendsData.push(legendItem.name);
    });
    chartData['legends'] = legendsData;
  }

  chartData['series'] = chartOptions.series.map(chartSeriesData => {
    let seriesData = {};
    seriesData['name'] = chartSeriesData.name;
    if (chartSeriesData.name === 'Usage') {
      seriesData['data'] = chartSeriesData.data.map(data => {
        return {
          name: data.name,
          value: data.y
        }
      });
    } else {
      seriesData['data'] = chartSeriesData.data;
    }
    return seriesData;
  });
  let shcData = {};
  if (chartModule === 'shc-data') {
    let shcSeriesMapping = {
      youSeries: {
        text: 'yourHomeText',
        value: 'yourHomeValue'
      },
      efficientSeries: {
        text: 'efficientHomesText',
        value: 'efficientHomesValue'
      }
    };
    shcData['title'] = convertHtmlToText(chartOptions.title.text);
    chartOptions.series[0].data.forEach((shcSeriesData) => {
      let seriesName = convertHtmlToText(shcSeriesData.name);
      shcData[`${shcSeriesMapping[shcSeriesData.seriesId]['text']}`] = seriesName;
      shcData[`${shcSeriesMapping[shcSeriesData.seriesId]['value']}`] = shcSeriesData.y;
    });
    chartData = shcData;
  }
  return chartData;
};

export const isContentOverflowing = (el) => {
  let overflowing = false;
  if (el) {
    overflowing = el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight;
  }

  return overflowing;
};

const convertHtmlToText = (htmlContent) => {
  var tmp = document.createElement("DIV");
  tmp.innerHTML = htmlContent;
  let text = tmp.textContent || tmp.innerText || "";
  return text.trim();
}